import React, { useContext, useEffect, useState } from "react";
import Header from "../../components/HeaderComponent";
import {
  useTheme,
  useApprove,
  useManualUpload,
  useUserLogin,
  usePagination,
} from "../../hooks/index";
import { AuthContext } from "../../components/AuthContextComponent";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
} from "../../components/table/index";
import SecurityQuestion from "../../components/SecurityQuestion";
import { STATUS } from "../../lib/enums/index";
import Pagination from "../../components/pagination/Pagination";
import Column from "../../components/grid/Column";
import Modal from "../../components/modal/ModalComponent";
import {
  formatDate,
  getApprovalStatusStyle,
  getApprovalTotalAmountStyle,
} from "../../lib/formatter/Formatter";
import LoadingComponent from "../../components/loading/Loading";
import { approveHeader } from "../../lib/constants/index";
import { AutoLogout } from "../../components/AutoLogoutComponent";
import UpdatePassword from "../../components/updatepassword/UpdatePassword";
import useHeadComponent from "../../hooks/HeaderHook";
import { FetchErr } from "../../lib/helpers/UnhandledError";

const Approve = () => {
  const { username, userInfo } = useContext(AuthContext);
  const { selectedPartner, openOTP } = useManualUpload();
  const { Balance } = useHeadComponent(selectedPartner);
  const { theme } = useTheme();
  const { customColor } = useUserLogin();
  const {
    batchNumber,
    forApproval,
    isRejectModalVisible,
    loading,
    noData,
    redirectModal,
    fetchCurrentUserQuestion,
    handleGetForApproval,
    handleRejectChange,
    handleReason,
    handleReasonSubmit,
    handleUpdateForApproval,
    setLoading,
    setIsRejectModalVisible,
  } = useApprove();

  const [approvalStatus, setApprovalStatus] = useState(false);

  const themeBackground = userInfo?.theme === "DARK" ? "#24292e" : "";
  const themeTextColor = userInfo?.theme === "DARK" ? "#fafbfc" : "";
  const { pwdUpdate, category } = userInfo || {};

  const [renderContent, setRenderContent] = useState<React.ReactNode>(null);

  const redirectState = !!(redirectModal && category === "EXTERNAL");

  const firstTimeLoginState = !!(pwdUpdate && category === "EXTERNAL");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); // Set loading to true when fetching starts
      try {
        await handleGetForApproval();
        await fetchCurrentUserQuestion();
        // You can call other fetching functions here if needed
        setTimeout(() => {
          if (pwdUpdate && category === STATUS.EXTERNAL) {
            setRenderContent(<UpdatePassword />);
          } else if (redirectModal && category === STATUS.EXTERNAL) {
            setRenderContent(<SecurityQuestion />);
          } else {
            setRenderContent(null);
          }
        }, 500);
      } catch (error) {
        FetchErr(error, themeBackground, customColor, themeTextColor);
      } finally {
        setLoading(false);
        setApprovalStatus(false);
      }
    };

    fetchData(); // Call the fetching function.
  }, [username, approvalStatus]);

  const rows = forApproval?.forApprovalInfo || [];
  const { currentItems, currentPage, totalPages, handlePageChange } =
    usePagination(rows);

  return (
    <div className="w-full">
      <Header Balance={Balance} theme={theme}></Header>

      <AutoLogout
        openOTP={openOTP}
        redirectModal={redirectState}
        redirectChangePassword={firstTimeLoginState}
        isRejection={isRejectModalVisible}
      />

      <Column>
        {loading ? (
          <>
            <div className="flex justify-center max-w-[1240px] mx-auto px-4 h-full items-center p-96">
              <LoadingComponent customColor={customColor} />
            </div>
          </>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow data={approveHeader} isHeader />
              </TableHeader>
              <TableBody>
                {rows && forApproval && currentItems ? (
                  <>
                    {currentItems.map((rowData: any, index: number) => (
                      <TableRow
                        key={index}
                        index={index}
                        data={[
                          index + 1 + (currentPage - 1) * 15,
                          // rowCount,
                          formatDate(rowData.transDate),
                          rowData.userId,
                          rowData.batchNumber,
                          rowData.total,
                          <span
                            style={getApprovalTotalAmountStyle(
                              rowData.origTotal.toLocaleString()
                            )}
                          >
                            {rowData.origTotal.toLocaleString()}
                          </span>,
                          <span
                            style={getApprovalStatusStyle(
                              rowData.approvalStatus
                            )}
                          >
                            {rowData.approvalStatus}
                          </span>,
                        ]}
                        isApprove
                        onApprove={() =>
                          handleUpdateForApproval(rowData.batchNumber)
                        }
                        onDecline={() =>
                          handleRejectChange(rowData.batchNumber)
                        }
                      />
                    ))}
                  </>
                ) : (
                  <>
                    <tr>
                      <td colSpan={8} className="py-5 text-center">
                        {noData}
                      </td>
                    </tr>
                  </>
                )}
              </TableBody>
            </Table>

            {rows && forApproval && currentItems ? (
              <Pagination
                isPendingRequestPage
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            ) : null}
          </>
        )}
      </Column>

      {isRejectModalVisible && (
        <Modal
          isClose={false} // Close the modal
          batchNumber={batchNumber}
          header=""
          label="Please state the reason for rejecting this batch."
          userId={""}
          otpTimer={0}
          otpType={""}
          type="REJECT"
          modalClose={setIsRejectModalVisible}
          onClick={() => handleReasonSubmit}
          toggleClose={() => handleReason(false)}
          setApprovalStatus={setApprovalStatus}
        />
      )}

      {renderContent}
    </div>
  );
};

export default Approve;
